import { z } from "zod";
import { stamps, PaginationQuery, Uuid } from "./common.js";
import {
  canonicalType,
  classificationSource,
  confidence,
  productDomain,
} from "./enums.js";

/**
 * Parsed comparability attributes (ported from `products/build_comparables.py`):
 * brand-of-first-token, TYPE_RULES canonical type, inch->mm sizes, radiator
 * h x l x panel-type, domain classifier and norm_text synonym folding.
 */
export const ParsedAttrs = z.object({
  /** Brand token parsed from the description (not the FK — see `brandId` on ProductDto). */
  brand: z.string().nullable(),
  canonicalType: canonicalType,
  domain: productDomain,
  /** Sizes in mm, ascending (e.g. [15] or [32, 40]). */
  sizeMm: z.array(z.number()),
  /** Canonical signature of `sizeMm` used for blocking (e.g. "15" / "32x40"). */
  sizeSignature: z.string(),
  packQty: z.number().int().positive().nullable(),
  uom: z.string().nullable(),
  /** Radiator panel type (e.g. "K1", "K2", "P+"). */
  radiatorType: z.string().nullable(),
  /** Radiator height in mm. */
  radiatorH: z.number().nullable(),
  /** Radiator length in mm. */
  radiatorL: z.number().nullable(),
  /** Normalised description text (synonym-folded) used by the scorer. */
  normText: z.string(),
});
export type ParsedAttrs = z.infer<typeof ParsedAttrs>;

/** Canonical catalogue product with parsed comparability attributes. */
export const ProductDto = z.object({
  id: Uuid,
  /** Supplier iQ code, when known. */
  iqCode: z.string().nullable(),
  /** Manufacturer part number, when known. */
  manPartNumber: z.string().nullable(),
  description: z.string(),
  brandId: Uuid.nullable(),
  ...ParsedAttrs.shape,
  classificationSource: classificationSource,
  confidence: confidence,
  needsReview: z.boolean(),
  ...stamps,
});
export type Product = z.infer<typeof ProductDto>;

/**
 * `PATCH /api/products/:id/classification` body — human correction, recorded
 * in `classification_overrides` so re-runs are free.
 */
export const ClassificationInput = z.object({
  canonicalType: canonicalType.optional(),
  domain: productDomain.optional(),
  sizeMm: z.array(z.number()).optional(),
  packQty: z.number().int().positive().nullish(),
  uom: z.string().nullish(),
  brandId: Uuid.nullish(),
});
export type ClassificationInput = z.infer<typeof ClassificationInput>;

/** `GET /api/products` query (`?search=&brandId=&domain=&needsReview=` + pagination). */
export const ProductSearchQuery = PaginationQuery.extend({
  search: z.string().optional(),
  brandId: Uuid.optional(),
  domain: productDomain.optional(),
  needsReview: z.stringbool().optional(),
});
export type ProductSearchQuery = z.infer<typeof ProductSearchQuery>;
